import React from "react";
import {
  Box,
  Container,
  CssBaseline,
  Typography,
  Paper,
  Button,
  Stack,
  Divider,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { useNavigate } from "react-router-dom";

const ThankYouForOrder = () => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 4, sm: 8 } }}>
      <CssBaseline />

      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 5 },
          borderRadius: "24px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 20px 40px -15px rgba(15, 23, 42, 0.08)",
          textAlign: "center",
          bgcolor: "#ffffff",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Subtle decorative top accent bar */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "6px",
            background: "linear-gradient(90deg, #10b981, #059669, #2f5d7e)",
          }}
        />

        {/* Celebratory Check Badge */}
        <Box
          sx={{
            width: 84,
            height: 84,
            borderRadius: "50%",
            bgcolor: "#ecfdf5",
            border: "2px solid #a7f3d0",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
            boxShadow: "0 8px 24px rgba(16, 185, 129, 0.2)",
            animation: "pulse 2s infinite ease-in-out",
            "@keyframes pulse": {
              "0%": { transform: "scale(1)" },
              "50%": { transform: "scale(1.05)" },
              "100%": { transform: "scale(1)" },
            },
          }}
        >
          <CheckCircleRoundedIcon sx={{ fontSize: 52, color: "#059669" }} />
        </Box>

        {/* Headings */}
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            color: "#0f172a",
            mb: 1,
            letterSpacing: "-0.02em",
            fontSize: { xs: "1.75rem", sm: "2.1rem" },
          }}
        >
          Order Confirmed!
        </Typography>

        <Typography
          variant="body1"
          sx={{ color: "text.secondary", maxWidth: 420, mx: "auto", mb: 3 }}
        >
          Thank you for your purchase! We've received your order and our team is
          already preparing it for dispatch.
        </Typography>

        {/* Highlights / Delivery Info Box */}
        <Box
          sx={{
            bgcolor: "#f8fafc",
            borderRadius: "16px",
            border: "1px solid #f1f5f9",
            p: 2.5,
            mb: 4,
            textAlign: "left",
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <EmailOutlinedIcon
                sx={{ color: "primary.main", fontSize: 20, mt: 0.2 }}
              />
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, color: "#1e293b", fontSize: "0.88rem" }}
                >
                  Confirmation Email Sent
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", display: "block" }}
                >
                  We have sent invoice and receipt details to your registered email address.
                </Typography>
              </Box>
            </Stack>

            <Divider sx={{ borderColor: "#e2e8f0" }} />

            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <LocalShippingOutlinedIcon
                sx={{ color: "#059669", fontSize: 20, mt: 0.2 }}
              />
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, color: "#1e293b", fontSize: "0.88rem" }}
                >
                  Estimated Delivery
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", display: "block" }}
                >
                  Expected within 3-5 business days. Real-time tracking updates will follow.
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </Box>

        {/* Action Buttons */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="center"
        >
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate("/userOrders")}
            endIcon={<ArrowForwardRoundedIcon />}
            sx={{
              py: 1.4,
              px: 3,
              borderRadius: "12px",
              fontWeight: 700,
              textTransform: "none",
              fontSize: "0.95rem",
              boxShadow: "0 4px 14px rgba(47, 93, 126, 0.3)",
            }}
          >
            Track Your Orders
          </Button>

          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate("/")}
            startIcon={<ShoppingBagOutlinedIcon />}
            sx={{
              py: 1.4,
              px: 3,
              borderRadius: "12px",
              fontWeight: 700,
              textTransform: "none",
              fontSize: "0.95rem",
              borderColor: "#cbd5e1",
              color: "#334155",
              "&:hover": {
                borderColor: "#94a3b8",
                bgcolor: "#f8fafc",
              },
            }}
          >
            Continue Shopping
          </Button>
        </Stack>

        {/* Support note */}
        <Typography
          variant="caption"
          sx={{
            display: "block",
            color: "#94a3b8",
            mt: 4,
            fontSize: "0.78rem",
          }}
        >
          Questions about your order? Reach us at{" "}
          <Box
            component="a"
            href="mailto:support@inproveda.com"
            sx={{
              color: "primary.main",
              textDecoration: "none",
              fontWeight: 600,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            support@inproveda.com
          </Box>
        </Typography>
      </Paper>
    </Container>
  );
};

export default ThankYouForOrder;
